import type { TestimonialsContent } from '@mawkaa/types';
import { Star, Quote } from 'lucide-react';
import { cn } from '@mawkaa/ui';

interface TestimonialsBlockProps {
  content: TestimonialsContent;
  isEditing?: boolean;
}

export function TestimonialsBlock({ content, isEditing }: TestimonialsBlockProps) {
  const { title, testimonials = [], style = 'cards' } = content;

  return (
    <section className="px-4 py-16 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {title && (
          <h2 className="text-3xl font-bold text-center mb-12">{title}</h2>
        )}

        <div className={cn(
          'grid gap-8',
          style === 'cards' && 'md:grid-cols-2 lg:grid-cols-3',
          style === 'carousel' && 'md:grid-cols-1'
        )}>
          {testimonials.length > 0 ? (
            testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-sm"
              >
                <Quote className="w-8 h-8 text-primary/20 mb-4" />

                {testimonial.rating && (
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          'w-4 h-4',
                          i < testimonial.rating! ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'
                        )}
                      />
                    ))}
                  </div>
                )}

                <p className="text-muted-foreground mb-6">{testimonial.content}</p>

                <div className="flex items-center gap-3">
                  {testimonial.avatar ? (
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.author}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary font-medium">
                        {testimonial.author[0]}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{testimonial.author}</p>
                    {(testimonial.role || testimonial.company) && (
                      <p className="text-sm text-muted-foreground">
                        {testimonial.role}
                        {testimonial.role && testimonial.company && ', '}
                        {testimonial.company}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : isEditing ? (
            <div className="col-span-full text-center py-8 text-muted-foreground">
              Ajoutez des témoignages dans le panneau de propriétés
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
